import React, { useEffect, useState, useMemo } from "react";
import { isArray, isEmpty } from "lodash";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Setting } from "../../utils/Setting";
import { getApiData } from "../../utils/APIHelper";
import { ReactComponent as LeftArrowIcon } from "../../assets/svg/LeftArrowIcon.svg";
import { ReactComponent as BlackSmallDhiramIcon } from "../../assets/svg/BlackSmallDhiramIcon.svg";
import { Box, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { statusStyleArray } from "./config";
import tabs from "./config";
import rawStyles from "./styles";
import { formattedAmountWithoutAed } from "../../utils/CommonFunction";
import Milestones from "./components/Milestones";
import Budget from "./components/Budget";
import PaymentGroups from "./components/PaymentGroups";
import Changelog from "../OnGoing/Changelog";
import ProjectOverview from "./components/ProjectOverview";
import ContractorPayments from "./components/ContractorPayments";
import Inspections from "./components/Inspections";
import ProjectUpdates from "./components/ProjectUpdates";
import IssueLog from "./components/IssueLog";
import { useMediaQuery, useTheme } from "@mui/material";
import { useTokenValidation } from "../../components/hooks/useTokenValidation";
import SecurityPolicyModal from "../../components/SecurityPolicyModal";
import { renderCurrencyIcon } from "../../utils/CurrencyUtils";
import ProjectRoster from "../WorkforceManagementTab";
import { useCRUDPermission } from "../../components/hooks/usePermission";

const StyledTab = styled(Tab)(({ theme, selected }) => ({
  color: selected ? "#fff !important" : "#949DAA",
  backgroundColor: selected ? "#0d0d0d !important" : "#fff !important",
  border: "1px solid #d7d8d9",
  borderRadius: "10px",
  minHeight: "40px !important",
  fontWeight: 500,
  textTransform: "none",
  marginRight: "10px",
  padding: "4px 16px !important",
  fontFamily: "Inter Tight",
  "&:hover": {
    backgroundColor: selected ? "#0d0d0d" : "",
    color: !selected && "#000",
  },
  "&.Mui-disabled": {
    opacity: 0.7,
    cursor: "not-allowed !important",
    backgroundColor: "#f5f5f5 !important",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "transparent",
  },
}));

export const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return text;
  const displayText = text
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return displayText;
};

export default function NewProjectDetails() {
  const [loader, setLoader] = useState(false);
  const [details, setDetails] = useState({});
  const { id, tab, subTab } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Token validation hook
  const { showSecurityModal, handleSecurityModalClose } = useTokenValidation();

  // Permission checks for each tab
  const { hasPermission: hasProjectsRead } = useCRUDPermission("PROJECTS", "READ");
  const { hasPermission: hasMilestonesRead } = useCRUDPermission("MILESTONES", "READ");
  const { hasPermission: hasBudgetRead } = useCRUDPermission("BUDGET", "READ");
  const { hasPermission: hasPaymentGroupsRead } = useCRUDPermission("PAYMENT_GROUPS", "READ");
  const { hasPermission: hasPaymentsRead } = useCRUDPermission("CUSTOMER_PAYMENTS", "READ");
  const { hasPermission: hasChangeRequestsRead } = useCRUDPermission("CHANGE_REQUESTS", "READ");
  const { hasPermission: hasInspectionsRead } = useCRUDPermission("INSPECTIONS", "READ");
  const { hasPermission: hasMilestoneUpdatesRead } = useCRUDPermission("MILESTONE_UPDATES", "READ");
  const { hasPermission: hasWorkforceRead } = useCRUDPermission("WORKFORCE", "READ");

  // Define tab configurations with their permissions
  const allTabConfigs = useMemo(() => [
    { id: "1", title: "Project Overview", route: "", hasPermission: hasProjectsRead },
    { id: "2", title: "Milestones", route: "milestone", hasPermission: hasMilestonesRead },
    { id: "3", title: "Budget", route: "budget", hasPermission: hasBudgetRead },
    { id: "4", title: "Payment Groups", route: "payment-group", hasPermission: hasPaymentGroupsRead },
    { id: "5", title: "Payments", route: "payments", hasPermission: hasPaymentsRead },
    { id: "6", title: "Change Requests", route: "change-request", hasPermission: hasChangeRequestsRead },
    { id: "7", title: "Inspections", route: "inspections", hasPermission: hasInspectionsRead },
    { id: "8", title: "Progress Updates", route: "project-updates", hasPermission: hasMilestoneUpdatesRead },
    { id: "9", title: "Issues", route: "issue-logs", hasPermission: hasMilestoneUpdatesRead },
    { id: "10", title: "Workforce Management", route: "workforce-management", hasPermission: hasWorkforceRead },
  ], [hasProjectsRead, hasMilestonesRead, hasBudgetRead, hasPaymentGroupsRead, hasPaymentsRead, hasChangeRequestsRead, hasInspectionsRead, hasMilestoneUpdatesRead, hasWorkforceRead]);

  // Filter tabs based on permissions
  const visibleTabs = useMemo(() => allTabConfigs.filter(t => t.hasPermission), [allTabConfigs]);

  // Create route to original index mapping for content rendering
  const routeToOriginalIndex = {
    "": 0,
    "milestone": 1,
    "budget": 2,
    "payment-group": 3,
    "payments": 4,
    "change-request": 5,
    "inspections": 6,
    "project-updates": 7,
    "issue-logs": 8,
    "workforce-management": 9,
  };

  // Find current tab index in visible tabs
  const currentRoute = tab || "";
  const tabValue = visibleTabs.findIndex(t => t.route === currentRoute);
  const originalTabIndex = routeToOriginalIndex[currentRoute] ?? 0;

  const styles = rawStyles(isMobile);

  useEffect(() => {
    getProjectDetails();
  }, [id]);

  async function getProjectDetails() {
    setLoader(true);
    try {
      const response = await getApiData(
        `${Setting.endpoints.getProject}?proposal_id=${id}`,
        "GET",
        {}
      );
      if (
        response.success &&
        isArray(response?.data) &&
        !isEmpty(response?.data)
      ) {
        setDetails(response?.data[0]);
      }
      setLoader(false);
    } catch (error) {
      console.log("error:===>>>", error);
      setLoader(false);
    }
  }

  function handleTabChange(event, newValue) {
    const selectedTab = visibleTabs[newValue];
    const newTabRoute = selectedTab?.route
      ? `/project/details/${id}/${selectedTab.route}`
      : `/project/details/${id}`;

    navigate(newTabRoute);
  }

  let [searchParams, setSearchParams] = useSearchParams();

  const changeId = searchParams.get("change");

  function renderTabContent() {
    console.log("originalTabIndex", originalTabIndex);
    switch (originalTabIndex) {
      case 0:
        return (
          <ProjectOverview
            details={details}
            isMobile={isMobile}
            styles={styles}
          />
        );
      case 1:
        return (
          <Milestones
            details={details}
            getProjectDetails={getProjectDetails}
            isMobile={isMobile}
            styles={styles}
          />
        );
      case 2:
        return <Budget details={details} isMobile={isMobile} styles={styles} />;
      case 3:
        return (
          <PaymentGroups
            details={details}
            getProjectDetails={getProjectDetails}
            isMobile={isMobile}
            styles={styles}
          />
        );
      case 4:
        return (
          <ContractorPayments
            projectId={details?.id}
            isMobile={isMobile}
            styles={styles}
          />
        );
      case 5:
        return (
          <Changelog
            projectId={details?.id}
            isMobile={isMobile}
            styles={styles}
            projectDetails={details}
          />
        );
      case 6:
        return (
          <Inspections
            projectId={details?.id}
            isMobile={isMobile}
            styles={styles}
          />
        );
      case 7:
        return (
          <ProjectUpdates
            projectId={details?.id}
            projectDetails={details}
            isMobile={isMobile}
            styles={styles}
            subTab={subTab}
          />
        );
      case 8:
        return (
          <IssueLog
            projectId={details?.id}
            projectDetails={details}
            isMobile={isMobile}
          />
        );
      case 9:
        return (
          <ProjectRoster projectId={details?.id} />
        );
      default:
        return <div>Default Content</div>;
    }
  }

  return (
    <div style={{ height: "100%" }}>
      {/* Security Policy Modal */}
      <SecurityPolicyModal
        open={showSecurityModal}
        onConfirm={handleSecurityModalClose}
      />

      {loader ? (
        <center style={styles.header}>
          <CircularProgress />
        </center>
      ) : (
        <div
          style={{
            ...styles.container,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Fixed (non-scrollable) top section: header + summary + tabs */}
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: "#fff",
            }}
          >
            <header style={styles.header}>
              <div style={styles.headerContent}>
                <LeftArrowIcon
                  onClick={() => navigate("/dashboard")}
                  style={{ cursor: "pointer" }}
                />
                <div style={styles.idText}>
                  <span style={styles.idLabel}>ID:</span> {details?.project_slug}
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <Box
                    style={{
                      width: "15px",
                      height: "15px",
                      borderRadius: "50%",
                      backgroundColor:
                        statusStyleArray.find(
                          (style) => style.status === details?.status
                        )?.backgroundColor || "transparent",
                      border: "3px solid #e5e5e5",
                    }}
                  />
                  <Typography
                    style={{
                      fontSize: "14px",
                      marginLeft: "8px",
                      color: "#0D0D0E",
                    }}
                  >
                    {getStatusDisplayText(details?.status)}
                  </Typography>
                </div>
              </div>
            </header>

            <div style={styles.subContainer}>
              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: isMobile && "2rem",
                }}
              >
                <div style={{ ...styles.rowFlex, gap: "15px", flex: "2" }}>
                  <Box
                    component="img"
                    src={
                      details?.project_image?.[0]?.image
                        ? details?.project_image?.[0]?.image
                        : details?.default_image?.[0]?.image
                    }
                    alt="Project Icon"
                    sx={{
                      width: 90,
                      height: 90,
                      borderRadius: "16px",
                      marginRight: 1,
                    }}
                  />
                  <div style={styles.columnFlex}>
                    <div style={{ ...styles.rowFlex, gap: "15px" }}>
                      <p style={styles.startDate}>
                        Start Date: {details?.start_date}
                      </p>
                    </div>
                    <h2 style={styles.projectHeading}>{details?.name}</h2>
                    <p style={styles.endDate}>
                      Moving Date: {details?.end_date}
                    </p>
                  </div>
                </div>

                <div style={{ ...styles.columnFlex, flex: "1" }}>
                  <p style={styles.idLabel}>Cost</p>
                  <p style={styles.price}>
                    <span
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "2px",
                      }}
                    >
                      {renderCurrencyIcon("black-small")}
                      {formattedAmountWithoutAed(
                        details?.proposal_status !== "ongoing"
                          ? details?.budget
                          : details?.budget_without_commission
                      )}
                    </span>
                  </p>
                </div>
              </div>

              {!changeId && (
                <Tabs
                  value={tabValue >= 0 ? tabValue : 0}
                  variant="scrollable"
                  onChange={handleTabChange}
                  sx={{
                    marginTop: "3vh",
                    "& .MuiTabs-indicator": {
                      background: "none",
                    },
                    "& .MuiTabs-scrollButtons": {
                      display: "none",
                    },
                  }}
                >
                  {visibleTabs?.map((tabConfig, index) => (
                    <StyledTab
                      key={tabConfig.id}
                      label={tabConfig.title}
                      selected={tabValue === index}
                      disabled={tabConfig.route === "project-updates" && details?.status !== "ongoing"}
                    />
                  ))}
                </Tabs>
              )}

            </div>
          </div>

          {/* Scrollable content section: everything under tabs */}
          <div
            style={{
              ...styles.subContainer,
              paddingTop: 0,
              flex: 1,
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {renderTabContent()}
          </div>
        </div>
      )}
    </div>
  );
}
